// src/Dashboard/pages/user/News/components/NewsReadDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import createDOMPurify from "dompurify";
import {
  FaArrowsAlt,
  FaCrop,
  FaTimes,
} from "react-icons/fa";

import { toAbsolutePhotoUrl } from "../../../../../services/newsPhotoServices";

import {
  FALLBACK_NEWS_IMAGE,
  formatNewsDate,
  getAuthorName,
  getNewsDate,
  onImgError,
} from "../utils/newsDashboardUtils";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

/* -------------------------------------------------------
   DOMPurify et styles enrichis sécurisés
-------------------------------------------------------- */

const purify =
  typeof window !== "undefined" ? createDOMPurify(window) : null;

const isColor = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  return (
    text === "transparent" ||
    text === "currentcolor" ||
    text === "inherit" ||
    /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(text) ||
    /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      text,
    ) ||
    /^hsla?\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      text,
    ) ||
    /^[a-z]+$/i.test(text)
  );
};

const isSafeIndentSize = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  if (text === "0") return true;

  const match = text.match(/^(\d+(?:\.\d+)?)(px|rem|em)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const maximumByUnit = {
    px: 160,
    rem: 10,
    em: 10,
  };

  return amount >= 0 && amount <= maximumByUnit[unit];
};

const isSafeFontSize = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  const match = text.match(/^(\d+(?:\.\d+)?)(px|pt|rem|em|%)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const limits = {
    px: { min: 8, max: 64 },
    pt: { min: 6, max: 48 },
    rem: { min: 0.5, max: 4 },
    em: { min: 0.5, max: 4 },
    "%": { min: 50, max: 300 },
  };

  return amount >= limits[unit].min && amount <= limits[unit].max;
};

const isSafeFontFamily = (value) => {
  const text = String(value || "").trim();

  if (!text || text.length > 120) return false;

  if (
    text.includes(";") ||
    text.includes("{") ||
    text.includes("}") ||
    text.includes("\\") ||
    /url\s*\(/i.test(text) ||
    /expression\s*\(/i.test(text) ||
    /var\s*\(/i.test(text)
  ) {
    return false;
  }

  return /^[-"'a-z0-9À-ÿ_,\s]+$/i.test(text);
};

const isSafeLineHeight = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  if (/^\d+(?:\.\d+)?$/.test(text)) {
    const amount = Number(text);

    return amount >= 0.8 && amount <= 3;
  }

  const match = text.match(/^(\d+(?:\.\d+)?)(px|rem|em|%)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const limits = {
    px: { min: 8, max: 80 },
    rem: { min: 0.8, max: 5 },
    em: { min: 0.8, max: 5 },
    "%": { min: 80, max: 300 },
  };

  return amount >= limits[unit].min && amount <= limits[unit].max;
};

const sanitizeInlineStyle = (styleText = "") => {
  const ALLOWED_STYLES = {
    color: (value) => isColor(value),

    "background-color": (value) => isColor(value),

    "text-align": (value) =>
      ["left", "right", "center", "justify"].includes(
        String(value).trim().toLowerCase(),
      ),

    "font-weight": (value) =>
      /^(normal|bold|bolder|lighter|[1-9]00)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "font-style": (value) =>
      /^(normal|italic|oblique)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "font-size": (value) => isSafeFontSize(value),

    "font-family": (value) => isSafeFontFamily(value),

    "line-height": (value) => isSafeLineHeight(value),

    "text-decoration": (value) =>
      /^(none|underline|line-through|overline)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "margin-left": (value) => isSafeIndentSize(value),

    "padding-left": (value) => isSafeIndentSize(value),

    "text-indent": (value) => isSafeIndentSize(value),

    "list-style-type": (value) =>
      [
        "none",
        "disc",
        "circle",
        "square",
        "decimal",
        "lower-alpha",
        "upper-alpha",
        "lower-roman",
        "upper-roman",
      ].includes(String(value).trim().toLowerCase()),
  };

  return String(styleText)
    .split(";")
    .map((rule) => rule.trim())
    .filter(Boolean)
    .map((rule) => {
      const separatorIndex = rule.indexOf(":");

      if (separatorIndex === -1) return null;

      const property = rule
        .slice(0, separatorIndex)
        .trim()
        .toLowerCase();

      const value = rule.slice(separatorIndex + 1).trim();

      return ALLOWED_STYLES[property]?.(value)
        ? `${property}: ${value}`
        : null;
    })
    .filter(Boolean)
    .join("; ");
};

if (purify) {
  purify.removeAllHooks();

  purify.addHook("uponSanitizeAttribute", (node, data) => {
    if (data.attrName === "style") {
      const cleanStyle = sanitizeInlineStyle(data.attrValue);

      if (cleanStyle) {
        data.attrValue = cleanStyle;
      } else {
        data.keepAttr = false;
      }
    }
  });

  purify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName !== "A") return;

    const href = node.getAttribute("href") || "";

    if (/^\s*javascript:/i.test(href)) {
      node.removeAttribute("href");
    }

    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  });
}

/* -------------------------------------------------------
   Classes du contenu enrichi
-------------------------------------------------------- */

const RICH_TEXT_CLASSES = cn(
  "news-read-drawer-rich-content",
  "prose prose-slate max-w-none break-words",
  "text-[15px] leading-7 text-slate-700 sm:text-base sm:leading-8",
  "dark:prose-invert dark:text-slate-300",

  // Paragraphes et retours à la ligne
  "prose-p:my-4",
  "[&_p]:whitespace-pre-wrap",

  // Titres
  "prose-headings:my-4",
  "prose-headings:font-barlow",
  "prose-headings:font-bold",
  "prose-headings:text-slate-950",
  "dark:prose-headings:!text-white",

  // Texte enrichi
  "prose-strong:text-slate-950",
  "prose-b:text-slate-950",
  "dark:prose-strong:!text-white",
  "dark:prose-b:!text-white",
  "prose-a:font-bold",
  "prose-a:text-sky-600",
  "hover:prose-a:underline",
  "dark:prose-a:text-sky-400",

  // Listes à puces
  "[&_ul]:!my-5",
  "[&_ul]:!list-disc",
  "[&_ul]:!pl-7",

  // Listes numérotées
  "[&_ol]:!my-5",
  "[&_ol]:!list-decimal",
  "[&_ol]:!pl-7",

  // Éléments des listes
  "[&_li]:!my-1",
  "[&_li]:!pl-1",
  "[&_li]:whitespace-pre-wrap",

  // Sous-listes à puces
  "[&_ul_ul]:!my-2",
  "[&_ul_ul]:!list-[circle]",
  "[&_ul_ul]:!pl-6",
  "[&_ul_ul_ul]:!list-[square]",

  // Sous-listes numérotées
  "[&_ol_ol]:!my-2",
  "[&_ol_ol]:!list-[lower-alpha]",
  "[&_ol_ol]:!pl-6",
  "[&_ol_ol_ol]:!list-[lower-roman]",

  // Alignements Quill
  "[&_.ql-align-center]:!text-center",
  "[&_.ql-align-right]:!text-right",
  "[&_.ql-align-justify]:!text-justify",

  // Indentations Quill
  "[&_.ql-indent-1]:!ml-6",
  "[&_.ql-indent-2]:!ml-12",
  "[&_.ql-indent-3]:!ml-16",
  "[&_.ql-indent-4]:!ml-20",
  "[&_.ql-indent-5]:!ml-24",

  // Tailles Quill
  "[&_.ql-size-small]:!text-sm",
  "[&_.ql-size-large]:!text-xl",
  "[&_.ql-size-huge]:!text-3xl",

  // Polices Quill
  "[&_.ql-font-serif]:!font-serif",
  "[&_.ql-font-monospace]:!font-mono",

  // Citations
  "prose-blockquote:my-5",
  "prose-blockquote:border-[#013E8E]",
  "prose-blockquote:bg-blue-50",
  "prose-blockquote:px-5",
  "prose-blockquote:py-3",
  "prose-blockquote:not-italic",
  "dark:prose-blockquote:bg-blue-500/10",

  // Images intégrées
  "prose-img:mx-auto",
  "prose-img:rounded-xl",
  "prose-img:shadow-md",

  // Tableaux
  "prose-table:overflow-hidden",
  "prose-table:rounded-xl",
  "prose-table:border",
  "prose-table:border-slate-200",
  "dark:prose-table:border-slate-700",

  // Séparateurs et code
  "prose-hr:border-slate-200",
  "dark:prose-hr:border-white/10",
  "prose-code:break-words",

  // Marges externes
  "[&>*:first-child]:mt-0",
  "[&>*:last-child]:mb-0",
);

/* -------------------------------------------------------
   Helpers
-------------------------------------------------------- */

const stripHtml = (html = "") => {
  if (typeof document === "undefined") {
    return String(html || "").replace(/<[^>]*>/g, "").trim();
  }

  const div = document.createElement("div");
  div.innerHTML = html;

  return (div.textContent || div.innerText || "").trim();
};

const sanitizeHtml = (html = "") => {
  if (!purify) return "";

  return purify.sanitize(String(html || ""), {
    USE_PROFILES: {
      html: true,
    },
    ADD_ATTR: [
      "style",
      "target",
      "rel",
      "class",
      "data-list",
    ],
    ALLOW_DATA_ATTR: true,
    FORBID_TAGS: ["style"],
  });
};

/* -------------------------------------------------------
   Composant
-------------------------------------------------------- */

export default function NewsReadDrawer({
  isOpen,
  news,
  onClose,
}) {
  const [fit, setFit] = useState("cover");
  const [objectPosition, setObjectPosition] =
    useState("center");

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose]);

  /*
   * Réinitialise le cadrage lors du changement d’actualité.
   */
  useEffect(() => {
    if (!isOpen) return;

    setFit("cover");
    setObjectPosition("center");
  }, [isOpen, news?.id]);

  const cover = useMemo(() => {
    return (
      toAbsolutePhotoUrl(news?.image_url || "") ||
      FALLBACK_NEWS_IMAGE
    );
  }, [news?.image_url]);

  const safeContent = useMemo(() => {
    const rawContent = news?.content || "";
    const plainContent = stripHtml(rawContent);

    if (!plainContent) {
      return sanitizeHtml(
        '<p style="font-style: italic;">Contenu vide.</p>',
      );
    }

    return sanitizeHtml(rawContent);
  }, [news?.content]);

  if (!isOpen) return null;

  const authorName = getAuthorName(news);
  const date = getNewsDate(news);

  const handleImageLoad = (event) => {
    const { naturalWidth, naturalHeight } =
      event.currentTarget;

    if (naturalHeight > naturalWidth) {
      setFit("contain");
      setObjectPosition("center 12%");
    } else {
      setFit("cover");
      setObjectPosition("center");
    }
  };

  const toggleImageFit = () => {
    setFit((currentFit) =>
      currentFit === "cover" ? "contain" : "cover",
    );
  };

  return (
    <div className="fixed inset-0 z-[120]">
      {/* Arrière-plan */}

      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        className="absolute inset-y-0 right-0 flex w-full max-w-5xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900"
        role="dialog"
        aria-modal="true"
        aria-label={`Lecture de l’actualité ${
          news?.title || "Sans titre"
        }`}
      >
        <div className="h-1.5 shrink-0 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        {/* En-tête */}

        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 px-4 py-4 dark:border-slate-800 sm:px-6">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
              Lecture actualité
            </p>

            <h2 className="line-clamp-2 text-lg font-bold text-slate-900 dark:text-white sm:text-xl">
              {news?.title || "Sans titre"}
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 transition hover:bg-slate-200 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#013E8E]/30 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </header>

        {/* Corps scrollable */}

        <div className="flex-1 overflow-y-auto">
          {/* Image */}

          <div className="relative bg-slate-100 dark:bg-slate-950">
            <div className="h-[320px] sm:h-[420px]">
              <img
                src={cover}
                alt={
                  news?.title || "Illustration actualité"
                }
                className={cn(
                  "h-full w-full",
                  fit === "contain"
                    ? "object-contain"
                    : "object-cover",
                )}
                style={{ objectPosition }}
                onLoad={handleImageLoad}
                onError={onImgError}
              />
            </div>

            <button
              type="button"
              onClick={toggleImageFit}
              title={
                fit === "contain"
                  ? "Recadrer l’image"
                  : "Afficher l’image entièrement"
              }
              className="absolute bottom-4 right-4 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white/90 text-slate-700 shadow-sm ring-1 ring-slate-200 transition hover:scale-105 hover:bg-white focus:outline-none focus-visible:ring-4 focus-visible:ring-[#013E8E]/30 dark:bg-slate-900/90 dark:text-slate-200 dark:ring-slate-700"
              aria-label={
                fit === "contain"
                  ? "Recadrer l’image"
                  : "Afficher toute l’image"
              }
            >
              {fit === "contain" ? (
                <FaCrop />
              ) : (
                <FaArrowsAlt />
              )}
            </button>
          </div>

          {/* Article */}

          <div className="p-4 sm:p-6">
            <div className="mb-5 rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
              <p className="text-sm text-slate-600 dark:text-slate-300">
                Publié le{" "}
                <span className="font-bold text-slate-900 dark:text-white">
                  {formatNewsDate(date)}
                </span>

                {authorName ? (
                  <>
                    {" "}
                    par{" "}
                    <span className="font-bold text-slate-900 dark:text-white">
                      {authorName}
                    </span>
                  </>
                ) : null}
              </p>

              {!news?.is_active ? (
                <span className="mt-3 inline-flex rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20">
                  Actualité masquée
                </span>
              ) : null}
            </div>

            {/* Contenu HTML enrichi sécurisé */}

            <div
              className={RICH_TEXT_CLASSES}
              style={{ tabSize: 4 }}
              dangerouslySetInnerHTML={{
                __html: safeContent,
              }}
            />
          </div>
        </div>
      </aside>
    </div>
  );
}